import { Article, Category } from '@wix/answers-api';
import { useExperiments } from '@wix/fe-essentials-standalone';
import type { NextPage } from 'next';
import { useContext } from 'react';
import { DeprecatedCategoryPage } from '../../components/DeprecatedCategoryPage';
import { CategoryPageWrapper } from '../../components/CategoryPageWrapper';
import { EMPTY_CATEGORY_ID, EXPERIMENTS, PAGES } from '../../constants';
import { Context } from '../../context';
import {
  getHelpCenterData,
  getCategoryArticles,
  getCategoryByUri,
  getPageSeo,
} from '../../services/answers';
import {
  GetWixServerSidePropsFunction,
  HttpStatusCode,
  PageSeo,
  PageType,
} from '../../types';
import { getExperiments, withAppServerProps } from '../../utils/appServerProps';
import { findCategoryInTree } from '../../utils/categories';
import { AspectStore } from '@wix/wix-bootstrap-greyhound-api';

type CategoryPageProps = {
  category: Category;
  subCategory?: Category;
  categoryArticles: Article[];
  categoriesArticles: Record<string, Article[]>;
};

const CategoryPage: NextPage<CategoryPageProps> = ({
  category,
  subCategory,
  categoryArticles,
  categoriesArticles,
}) => {
  const { categoriesTree } = useContext(Context);
  const { experiments } = useExperiments({ readOnly: true });

  const newCategoryPageEnabled = experiments.enabled(
    EXPERIMENTS.SPECS.SUB_SUB_CATEGORY_PAGE
  );

  return newCategoryPageEnabled ? (
    <CategoryPageWrapper
      currentCategory={category}
      categories={categoriesTree}
      categoriesArticles={categoriesArticles}
    />
  ) : (
    <DeprecatedCategoryPage
      category={category}
      subCategory={subCategory}
      categories={categoriesTree}
      categoryArticles={categoryArticles}
    />
  );
};

export const getServerSideProps: GetWixServerSidePropsFunction =
  withAppServerProps(async ({ req, res, locale, query }) => {
    const experiments = await getExperiments(
      res.locals.appContext,
      req,
      locale
    );

    const newCategoryPageEnabled =
      experiments[EXPERIMENTS.SPECS.SUB_SUB_CATEGORY_PAGE];

    const { categoryName } = query;

    let category: Category = await getCategoryByUri(
      req.aspects,
      `/${categoryName}`,
      locale,
      res.locals.redisClient
    );

    const isParent = category.parentId === EMPTY_CATEGORY_ID;

    const { categories: categoriesTree } = await getHelpCenterData(
      req.aspects,
      locale,
      res.locals.redisClient
    );
    const pageSeo = await getPageSeo(req.aspects, locale, PageType.Category);

    if (newCategoryPageEnabled === true || newCategoryPageEnabled === 'true') {
      return serverPropsNewVersion(
        req.aspects,
        category.id,
        pageSeo,
        categoriesTree,
        locale
      );
    }

    let subCategory;
    if (isParent) {
      category =
        categoriesTree.find((e: Category) => e.id === category.id) || category;

      if (category && category?.children && category?.children?.length > 0) {
        return {
          redirect: {
            statusCode: HttpStatusCode.MOVED_PERMANENTLY,
            destination: encodeURI(`/${locale}${category?.children[0].uri}`),
          },
        };
      }
      subCategory = category;
    } else {
      const childId = category.id;
      category =
        categoriesTree.find((e: Category) => e.id === category.parentId) ||
        category;
      subCategory =
        category?.children?.find((e: Category) => e.id === childId) || category;
    }

    let categoryArticles = [];
    categoryArticles = (
      await getCategoryArticles(req.aspects, [subCategory.id], locale)
    )[subCategory.id];

    return {
      props: {
        pageType: PAGES.CATEGORY,
        category,
        subCategory,
        categoryArticles,
        categoriesTree,
        pageData: {
          category: subCategory,
        },
        itemId: subCategory.id,
        pageSeo,
      },
    };
  });

const serverPropsNewVersion = async (
  aspects: AspectStore,
  categoryId: string,
  pageSeo: PageSeo,
  categoriesTree: Category[],
  locale: string
) => {
  const category: Category = findCategoryInTree(
    categoriesTree,
    categoryId
  ) as Category;
  let categoriesArticles: Record<string, Article[]> = {};
  if (!category.children || category.children.length === 0) {
    categoriesArticles = await getCategoryArticles(
      aspects,
      [category.id],
      locale
    );
  } else {
    const childrenIds = category.children.map((child: Category) => child.id);
    categoriesArticles = await getCategoryArticles(
      aspects,
      [...childrenIds],
      locale
    );
  }
  return {
    props: {
      pageType: PAGES.CATEGORY,
      category,
      categoriesTree,
      categoriesArticles,
      pageData: {
        category,
      },
      itemId: category.id,
      pageSeo,
    },
  };
};
export default CategoryPage;
